import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { MapPin, Send, Linkedin, Link, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO/SEO';
import { jobs } from '../data/jobs';
import ApplicationModal from '../components/ApplicationModal/ApplicationModal';

function JobDetails() {
  const { id } = useParams();
  const job = jobs.find(j => j.id === id);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!job) {
    return (
      <div className="bg-slate-50 min-h-screen pt-32 pb-20 px-4 text-center">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">Job Not Found</h1>
        <p className="text-slate-600 mb-8">The position you're looking for doesn't exist or has been filled.</p>
        <RouterLink to="/careers" className="text-blue-600 font-semibold hover:underline inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to Careers
        </RouterLink>
      </div>
    );
  }

  const handleCopyLink = () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    navigator.clipboard.writeText(url);
    alert("Link copied to clipboard!");
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(job.title + " at Cirronyx");

  return (
    <div className="bg-slate-50 min-h-screen">
      <SEO 
        title={`${job.title} | Careers at Cirronyx`}
        description={job.description}
        keywords={job.keywords}
        ogImage="/logo.png"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "JobPosting",
          "title": job.title,
          "description": job.description,
          "employmentType": job.type.includes("Full-Time") ? "FULL_TIME" : "INTERN",
          "hiringOrganization": {
            "@type": "Organization",
            "name": "Cirronyx",
            "sameAs": "https://cirronyx.com",
            "logo": "https://cirronyx.com/logo.png"
          },
          "jobLocation": {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Yaoundé",
              "addressCountry": "CM"
            }
          }
        }}
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto">
          <RouterLink to="/careers" className="text-blue-300 hover:text-white mb-8 inline-flex items-center gap-2 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to open positions
          </RouterLink>
          
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="bg-blue-500/20 text-blue-300 border border-blue-500/30 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
              {job.type}
            </span>
            {job.department && (
              <span className="bg-slate-800 text-slate-300 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                {job.department}
              </span>
            )}
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            {job.title}
          </h1>
          
          <div className="flex items-center text-slate-300 text-lg">
            <MapPin className="w-5 h-5 mr-2" />
            {job.location}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl border border-slate-200 p-8 md:p-12 -mt-24 relative z-10">
          <div className="prose max-w-none prose-slate prose-headings:font-bold prose-a:text-blue-600">
            {job.content}
          </div>
          
          <div className="mt-12 pt-8 border-t border-slate-100 flex flex-wrap items-center justify-between gap-6">
            <button onClick={() => setIsModalOpen(true)} className="bg-blue-600 text-white px-8 py-3 rounded-full font-bold hover:bg-blue-700 transition inline-flex items-center gap-2 group w-max">
              Apply Now
              <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
            
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-slate-500 mr-2">Share this role:</span>
              
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-[#0A66C2] hover:text-white transition-colors"
                title="Share on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              
              <a 
                href={`https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-[#25D366] hover:text-white transition-colors"
                title="Share on WhatsApp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.559 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>
              
              <button 
                onClick={handleCopyLink}
                className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors"
                title="Copy Link"
              >
                <Link className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <ApplicationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        jobTitle={job.title} 
      />
    </div>
  );
}

export default JobDetails;
